import { useIntl } from 'react-intl'
import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import type { ComponentProps } from 'react'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import loadable from 'components/utils/lazyImport'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE_SLUG,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import { TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import { isCoverGeniusEnabled } from 'lib/insurance/insuranceHelpers'
import AuthRoute from './AuthRoute'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import TripPlannerRoutes from 'tripPlanner/TripPlannerRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import LuxPlusMemberRoute from 'luxPlus/components/Routes/LuxPlusMemberRoute'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import AgentHubGuestPageRoutes from 'agentHub/routes/AgentHubGuestPageRoutes'
import { LE_AGENT_HUB_SOCIETE_CONFIRMATION } from 'agentHub/routes/AgentHubRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import type { LoadableComponent } from '@loadable/component'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { AppRedirect } from 'lib/routing/AppRedirect'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { LOYALTY_LANDING_PAGE, LOYALTY_OFFERS_HUB_PAGE, LOUNGE_PASSES_PAGE, LOYALTY_PURCHASE_POINTS_PAGE, LOYALTY_REWARDS_PAGE, TASTE_OF_GOLD_DETAILS_PAGE, ESIM_DETAILS_PAGE, ESIM_INFO_PAGE, TIER_PROGRESSION_DETAILS_PAGE, TIER_UPGRADE_CAMPAIGN_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyPurchasePointsEnabled from 'loyalty/selectors/getIsLoyaltyPurchasePointsEnabled'
import { isMaldivesCharterCarouselEnabled } from 'selectors/maldivesSelectors'
import CorporateEventRoutes from 'corporateEvents/CorporateEventRoutes'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { getAgentHubAffiliationRules, isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { isLEAdmin, isLEStaffOrAdmin } from 'selectors/accountSelectors'
import { CHARTER_BEFORE_YOU_FLY_PAGE_SLUG, CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG, FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM } from 'constants/friendsAndFamilyDiscount'
import RentalsPage from 'components/Pages/TypesPage/Rentals/RentalsPage'
import { MULTI_CLICK_UNSUB_SUPPORTED_BRANDS } from './constants'
import { LocaleAwareSwitch } from 'components/App/Routes/LocaleAwareRoute'
import TrackedRoute from './TrackedRoute'

const DestinationLandingPage = loadable(() => import('components/Pages/DestinationLandingPage/DestinationLandingPage'))
const CruiseLocationLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseLocationLandingPage'))
const CruiseShipsLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseShipsLandingPage'))
const HotelSearchFullScreenPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchFullScreenPage'))
const HotelSearchPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchPage'))
const MetaSearchPage = loadable(() => import('components/Pages/HotelSearchPage/MetaSearchPage'))
const HotelSearchMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchMapPage'))
const HotelSearchStoreMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchStoreMapPage'))
const SupportPage = loadable(() => import('components/Pages/SupportPage/SupportPage'))
const VideoChatPage = loadable(() => import('components/Pages/VideoChatPage'))
const AboutUsPage = loadable(() => import('components/Pages/AboutUsPage'))
const CareersPage = loadable(() => import('components/Pages/CareersPage'))
const StaticPage = loadable(() => import('components/Pages/StaticPage'))
const BestPricePage = loadable(() => import('components/Pages/BestPricePage'))
const FlightsLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage'))
const FlightsSearchLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage/FlightsSearchLandingPage'))
const FlightsSearchPage = loadable(() => import('components/Flights/FlightsSearchPage'))
const FlightsSearchResultsPage = loadable(() => import('components/Flights/FlightsSearchResultsPage'))
const FlightsSearchMultiCityResultsPage = loadable(() => import('components/Flights/FlightsSearchMultiCityResultsPage'))
const LegalPages = loadable(() => import('components/Pages/LegalPages'))
const ReferPage = loadable(() => import('marketing/pages/Refer'))
const VouchersPage = loadable(() => import('components/Pages/VouchersPage/VouchersPage'))
const NewsPage = loadable(() => import('components/Pages/NewsPage'))
const FrequentFlyerPage = loadable(() => import('components/Pages/FrequentFlyerPage'))
const MyEscapesPage = loadable(() => import('components/Pages/MyEscapesPage'))
const ShopOrdersPage = loadable(() => import('components/Pages/ShopOrdersPage'))
const UserReviews = loadable(() => import('components/Pages/UserReviews'))
const OrderPage = loadable(() => import('components/Pages/MyEscapesPage/OrderPage'))
const ShareOrderWebviewPage = loadable(() => import('components/Pages/MyEscapesPage/ShareOrderWebviewPage'))
const ShopOrderPage = loadable(() => import('components/Pages/ShopOrdersPage/OrderPage'))
const UpgradeRoomPage = loadable(() => import('components/Pages/MyEscapesPage/UpgradeRoomPage'))
const AirportTransferPage = loadable(() => import('components/Pages/MyEscapesPage/AirportTransfers'))
const PreferenceCentrePage = loadable(() => import('components/Pages/PreferenceCentrePage'))
const MySubscriptionsPage = loadable(() => import('components/Pages/MySubscriptionsPage'))
const MyWLSubscriptionsPage = loadable(() => import('components/Pages/MyWLSubscriptionsPage'))
const AccountDetailsPage = loadable(() => import('components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import('components/Pages/AccountDetailsTravellerPage'))
const CreditsAndPaymentPage = loadable(() => import('components/Pages/CreditsPage/CreditsAndPaymentPage'))
const TourTravellerInfoPage = loadable(() => import('components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const GiftCardConfirmationPage = loadable(() => import('components/Pages/GiftCardsPage/Confirmation'))
const GiftCardViewPage = loadable(() => import('components/Pages/GiftCardsPage/View'))
const LandingPage = loadable(() => import('components/Pages/LandingPage/LandingPage'))
const TravelInsurancePage = loadable(() => import('components/Pages/Insurance/LandingPage'))
const TravelInsuranceCoverGeniusPage = loadable(() => import('components/Pages/Insurance/LandingPage/CoverGenius'))
const TravelInsuranceQuote = loadable(() => import('components/Pages/Insurance/QuotePage'))
const TravelInsuranceAddCheckout = loadable(() => import('components/Pages/Insurance/AddCheckoutPage'))
const WeddingPage = loadable(() => import('weddings/pages/WeddingPage'))
const WeddingSetupPage = loadable(() => import('weddings/pages/WeddingSetupPage'))
const LeLiveLandingPage = loadable(() => import('leLive/pages/LeLiveLandingPage'))
const CallbackPage = loadable(() => import('supportAssistant/pages/CallbackPage'))
const TravelInsuranceDetails = loadable(() => import('components/Pages/Insurance/DetailsPage'))
const PassportCardDetailsPage = loadable(() => import('components/Pages/Insurance/PassportCardDetailsPage'))
const TravelInsurancePayment = loadable(() => import('components/Pages/Insurance/PaymentPage'))
const EscapesFinderPage = loadable(() => import('components/Pages/EscapesFinderPage'))
const ConfirmationPage = loadable(() => import('checkout/Components/Confirmation/ConfirmationPage'))
const BNBLLandingPage = loadable(() => import('components/Pages/BNBLLandingPage'))
const TWCLandingPage = loadable(() => import('components/Pages/TWCLandingPage'))
const EWCLandingPage = loadable(() => import('components/Pages/EWCLandingPage'))
const ContentHub = loadable(() => import('components/Pages/LandingPage/Hub'))
const OfferPage = loadable(() => import('components/OfferPage/OfferPage'))
const BedBankOfferPage = loadable(() => import('components/Pages/BedbankOfferPage'))
const PaginatedOffersPage = loadable(() => import('components/Pages/PaginatedOffersPage'))
const TourV2OfferPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferPage'))
const TourV2OfferVariationPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferVariationPage'))
const TourV2SearchPage = loadable(() => import('components/Pages/TourV2/SearchPage/TourV2SearchPage'))
const HotelsPage = loadable(() => import('components/Pages/TypesPage/Hotels/HotelsPage'))
const HomesAndVillasSearchPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchPage'))
const HomesAndVillasSearchMapPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchMapPage'))
const ToursPage = loadable(() => import('components/Pages/TypesPage/ToursPage'))
const UltraLuxVerticalPage = loadable(() => import('components/Pages/TypesPage/UltraLux/UltraLuxVerticalPage'))
const LuxBundlesPage = loadable(() => import('components/Pages/TypesPage/LuxBundlesPage'))
const DestinationProductPage = loadable(() => import('components/Pages/DestinationProductPage/DestinationProductPage'))
const GiftCardsPageContainer = loadable(() => import('components/Pages/GiftCardsPage'))
const PurchaseCreditsPage = loadable(() => import('components/Pages/PurchaseCreditsPage/PurchaseCreditsPage'))
const KlarnaLandingPage = loadable(() => import('components/Pages/PromotionalPages/KlarnaLandingPage'))
const AfterPayLandingPage = loadable(() => import('components/Pages/PromotionalPages/AfterPayLandingPage'))
const HoolahLandingPage = loadable(() => import('components/Pages/PromotionalPages/HoolahLandingPage'))
const RedirectToShopbackPage = loadable(() => import('components/Pages/PromotionalPages/RedirectToShopbackPage'))
const AtomeLandingPage = loadable(() => import('components/Pages/PromotionalPages/AtomeLandingPage'))
const WaaveLandingPage = loadable(() => import('components/Pages/PromotionalPages/WaaveLandingPage'))
const AccountPage = loadable(() => import('components/Pages/AccountPage/AccountLandingPage'))
const ExperienceOfferPage = loadable(() => import('components/Experiences/ExperienceOfferPage/ExperienceOfferPage'))
const ExperiencesPage = loadable(() => import('components/Experiences/ExperiencesPage/ExperiencesPage'))
const ExperienceSearchMapPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchMapPage'))
const CruisesPage = loadable(() => import('components/Cruises/CruisesPage/CruisesPage'))
const CruiseSearchPage = loadable(() => import('components/Cruises/SearchPage/CruiseSearchPage'))
const CruiseOfferPage = loadable(() => import('components/Cruises/CruiseOfferPage/CruiseOfferPage'))
const CruiseLineLandingPage = loadable(() => import('components/Cruises/CruiseLineLandingPage/CruiseLineLandingPage'))
const CruiseDealPage = loadable(() => import('components/Cruises/CruiseDealPage/CruiseDealPage'))
const CruiseDealsListPage = loadable(() => import('components/Cruises/CruiseDealsListPage/CruiseDealsListPage'))
const ExperienceSearchPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchPage'))
const TopPicksForMePage = loadable(() => import('components/Recommendations/TopPicksForMe/TopPicksForMePage'))
const RefereeLandingPage = loadable(() => import('../../../../content/pages/RefereeLanding'))
const GoogleHotelAdPage = loadable(() => import('../../../../marketing/pages/GoogleHotelAdPage'))
const CheckoutPageRoutes = loadable(() => import('checkout/routes'))
const CheckoutRestoreCartPage = loadable(() => import('checkout/Pages/RestoreCartPage'))
const CheckoutGatewayPage = loadable(() => import('checkout/Pages/GatewayPage'))
const RedirectCheckoutBNBL = loadable(() => import('checkout/Pages/RedirectCheckoutBNBL'))
const RedirectCheckoutChangeDates = loadable(() => import('checkout/Pages/RedirectCheckoutChangeDates'))
const RedirectCheckoutExtendDates = loadable(() => import('checkout/Pages/RedirectCheckoutExtendDates'))
const RedirectCheckoutAddExtraGuests = loadable(() => import(/* webpackChunkName: "RedirectCheckoutAddExtraGuests" */ 'checkout/Pages/RedirectCheckoutAddExtraGuests'))
const AncillaryPaymentPage = loadable(() => import('checkout/Pages/AncillaryPaymentPage'))
const AncillaryChangeReservationPage = loadable(() => import('checkout/Pages/AncillaryChangeReservationPage'))
const ExperienceVoucher = loadable(() => import('components/Pages/ExperienceVoucher'))
const PropertyPage = loadable(() => import('../../../../marketing/pages/PropertyPage'))
const RedeemGifted = loadable(() => import('components/Pages/RedeemGifted'))
const ExperienceGiftPrintPage = loadable(() => import('components/Pages/ExperienceGiftPrintPage'))
const ExperiencesListPage = loadable(() => import('components/Experiences/ExperiencesList/ExperiencesListPage'))
const AuCompetitionsPage = loadable(() => import('../../../../marketing/pages/AuCompetitionPage'))
const IntlCompetitionsPage = loadable(() => import('../../../../marketing/pages/INTLCompetitionPage'))
const ukCompetitionsPage = loadable(() => import('../../../../marketing/pages/UKCompetitionPage'))
const HolidayTypeLandingPage = loadable(() => import('components/Pages/HolidayTypeLandingPage'))
const CarHirePage = loadable(() => import('components/CarHire/CarHirePage/CarHirePage'))
const CarHireSearchPage = loadable(() => import('components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const HomePage = loadable(() => import('../../../../home/pages/HomePage/HomePage'))
const ContentfulWebPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/ContentfulWebPage'))
const LuxAwardPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/LuxAward/LuxAwardPage'))
const LuxPlusLandingPage = loadable(() => import('luxPlus/pages/LuxPlusLandingPage'))
const LuxPlusAccountPage = loadable(() => import('luxPlus/pages/LuxPlusAccountPage'))
const LoyaltyLandingPage = loadable(() => import('loyalty/pages/LoyaltyLandingPageContainer'))
const LoyaltyOffersHubPage = loadable(() => import('loyalty/pages/loyaltyOffersHubPage/LoyaltyOffersHubPage'))
const LoungePassesPage = loadable(() => import('loyalty/pages/loungePassesPage/LoungePassesPage'))
const LoyaltyPurchasePointsPage = loadable(() => import('loyalty/pages/loyaltyPurchasePointsPage/LoyaltyPurchasePointsPage'))
const LoyaltyRewardsPage = loadable(() => import('loyalty/pages/loyaltyRewardsPage/LoyaltyRewardsPage'))
const ResetPasswordPage = loadable(() => import('components/Pages/ResetPasswordPage/ResetPasswordPage'))
const FlightDealLandingPage = loadable(() => import('components/Flights/FlightDealLandingPage/FlightDealLandingPage'))
const FlightCampaignsLandingPage = loadable(() => import('components/Flights/FlightCampaignsLandingPage/FlightCampaignsLandingPage'))
const TravelPreferencesPage = loadable(() => import('components/Pages/AccountTravelPreferencesPage/TravelPreferencesPage'))
const AgentHubHomePage = loadable(() => import('agentHub/pages/AgentHubHomepage'))
const AgentHubAccountSetupCompleted = loadable(() => import('agentHub/pages/AgentHubAccountSetup/AgentHubAccountSetupCompleted'))
const AgentHubToursPage = loadable(() => import('agentHub/pages/AgentHubToursPage'))
const SocieteConfirmationPage = loadable(() => import('agentHub/pages/SocieteConfirmationPage'))
const SkyCheckConfirmationPage = loadable(() => import('components/Pages/EmailConfirmedPage/SkyCheckConfirmation/SkyCheckConfirmationPage'))
const TailorMadeTourRequestPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestPage'))
const TailorMadeTourRequestConfirmationPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestConfirmationPage'))
const SecurePaymentPage = loadable(() => import('components/Pages/SecurePaymentPage'))
const WhitelabelHomePage = loadable(() => import('whitelabel/pages/WhitelabelHomePage'))
const TravelCompanionsPage = loadable(() => import('components/Pages/TravelCompanionsPage'))
const UserCartPage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/MultiCartReviewWrapper'))
const SharedCartInvitePage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/SharedCartInvitePage'))
const TourCataloguePage = loadable(() => import('components/Pages/TourV2/TourCataloguePage'))
const TourDealPage = loadable(() => import('components/Tours/Deals/TourDealPage'))
const WishlistsIndexPage = loadable(() => import('wishlists/pages/WishlistsIndexPage'))
const WishlistDetailPage = loadable(() => import('wishlists/pages/WishlistDetailPage'))
const MerchandiseOfferPage = loadable(() => import('components/Merchandise/MerchandiseOfferPage/MerchandiseOfferPage'))
const MerchandiseLandingPage = loadable(() => import('components/Merchandise/MerchandiseLandingPage/MerchandiseLandingPage'))
const MerchandiseSearchPage = loadable(() => import('components/Merchandise/MerchandiseSearchPage/MerchandiseSearchPage'))
const MerchandiseBrandsPage = loadable(() => import('components/Merchandise/MerchandiseBrandsPage/MerchandiseBrandsPage'))
const MerchandiseCategoryPage = loadable(() => import('components/Merchandise/MerchandiseCategoryPage/MerchandiseCategoryPage'))
const MerchandiseCategoriesPage = loadable(() => import('components/Merchandise/MerchandiseCategoriesPage'))
const TasteOfGoldDetailsPage = loadable(() => import('takeovers/tasteOfGoldTakeover/pages/TasteOfGoldDetailsPage'))
const LoyaltyEsimDetailsPage = loadable(() => import('loyalty/pages/LoyaltyEsimDetailsPage'))
const LoyaltyEsimInfoPage = loadable(() => import('loyalty/pages/LoyaltyEsimInfoPage'))
const TierProgressionDetailsPage = loadable(() => import('loyalty/pages/TierProgressionDetailsPage'))
const TierUpgradeCampaignPage = loadable(() => import('loyalty/pages/tierUpgradeCampaign/TierUpgradeCampaignPage'))
const TriplePointsTakeoverLandingPage = loadable(() => import('takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverLandingPage'))
const DoublePointsTakeoverLandingPage = loadable(() => import('takeovers/societeDoublePointsTakeover/components/DoublePointTakeoverLandingPage'))
const SocieteNavBar = loadable(() => import('loyalty/components/SocieteNavBar'))
const MerchandiseNavBar = loadable(() => import('components/Merchandise/MerchandiseNavBar/MerchandiseNavBar'))
const ResubscribePage = loadable(() => import('components/Pages/ResubscribePage/ResubscribePage'))
const MultiClickUnsubPage = loadable(() => import('components/Pages/MultiClickUnsubPage/MultiClickUnsubPage'))
const MaldivesDirectSearchPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectSearchPage'))
const MaldivesDirectBeforeYouFlyPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectBeforeYouFlyPage/MaldivesDirectBeforeYouFlyPage'))
const FriendsAndFamilyInvitePage = loadable(() => import('components/FriendsAndFamilyDiscount/FriendsAndFamilyInvitePage/FriendsAndFamilyInvitePage'))
const AcceptOrderInvitePage = loadable(() => import('components/Pages/AcceptOrderInvitePage/AcceptOrderInvitePage'))

const promotionalPages: Record<string, LoadableComponent<unknown>> = {
  GiftCardsPageContainer,
  KlarnaLandingPage,
  AfterPayLandingPage,
  HoolahLandingPage,
  RedirectToShopbackPage,
  AtomeLandingPage,
  WaaveLandingPage,
}

interface Props {
  currentRegionCode: string
  standaloneFlightsEnabled: boolean
  standaloneCruiseEnabled: boolean
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
  tenant: string
}

function PageRoutes(props: Props) {
  const intl = useIntl()
  const {
    currentRegionCode,
    standaloneFlightsEnabled,
    standaloneCruiseEnabled,
    isLuxPlusEnabled,
    isLoyaltyEnabled,
  } = props

  const hasSetupAgentHubAccount = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')
  const agentHubAffiliationRules = useAppSelector(state => getAgentHubAffiliationRules(state))
  const isShopEnabled = useAppSelector(getIsMerchandiseEnabled)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const isStaff = useAppSelector(isLEStaffOrAdmin)
  const isAdmin = useAppSelector(isLEAdmin)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterFnfPage)

  const canBookCruise = !isAgentHubEnabled || agentHubAffiliationRules.canBookCruise
  const canBookCarHire = !isAgentHubEnabled || agentHubAffiliationRules.canBookCarHire
  const isLoyaltyLuxplusLandingPageEnabled = useOptimizelyExperiment(OptimizelyFeatureFlags.loyaltyLuxplusLandingPage)
  const isLoyaltyPurchasePointsEnabled = useAppSelector(getIsLoyaltyPurchasePointsEnabled)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterCarouselEnabled)

  return (
    <LocaleAwareSwitch>
      {/* This blocks access to all pages but the onboarding for agent hub until the user has signed up */}
      {config.agentHub.isEnabled && !hasSetupAgentHubAccount && <AgentHubGuestPageRoutes />}
      {/* Tour v1 redirects */}
      {Object.keys(config.toursV1Redirects).map(tourV1 =>
        <RedirectWithStatus
          key={tourV1}
          code={301}
          from={`/:regionCode/offer/${tourV1}`}
          to={`/tour/${config.toursV1Redirects[tourV1]}`}
          withSearchAndHash={false}
        />,
      )}
      {/* Tour v2 redirects */}
      {Object.keys(config.toursV2Redirects).flatMap(tourV2 => [
        <RedirectWithStatus
          key={`${tourV2}-variation`}
          code={301}
          from={`/:regionCode/tour/${tourV2}/:variationId`}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />,
        <RedirectWithStatus
          key={tourV2}
          code={301}
          from={`/:regionCode/tour/${tourV2}`}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />,
      ])}
      {/* Offer list Pages */}
      {config.agentHub.isEnabled && <TrackedRoute exact path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} component={AgentHubToursPage} />}
      <TrackedRoute exact path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} component={ToursPage} />
      <TrackedRoute exact path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`} component={HotelsPage} />
      <TrackedRoute
        exact
        path="/:regionCode/rentals"
        render={({ match }) => {
          if (match?.params.regionCode !== 'au') {
            return (
              <HttpStatus code={404}>
                <Error404PageWithOffers />
              </HttpStatus>
            )
          }
          return <RentalsPage />
        }}
      />
      <TrackedRoute exact path={`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`} component={LuxBundlesPage} />
      {config.ULTRALUX_ENABLED && <TrackedRoute exact path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`} component={UltraLuxVerticalPage} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <TrackedRoute exact path="/:regionCode/experiences" component={ExperiencesPage} />}
      {/* Cruises */}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruises" component={CruisesPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruises/deals" component={CruiseDealsListPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute path="/:regionCode/cruises/departures/:departureLocation" component={CruiseLocationLandingPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute path="/:regionCode/cruises/destinations/:destinationLocation" component={CruiseLocationLandingPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute path="/:regionCode/cruises/ships/:shipNames" component={CruiseShipsLandingPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruises/deal/:slug" component={CruiseDealPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruises/:offerId" component={CruiseOfferPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruises/:offerSlug/:offerId" component={CruiseOfferPage} />}
      {standaloneCruiseEnabled && canBookCruise && <TrackedRoute exact path="/:regionCode/cruise-line/:cruiseLineSlug" component={CruiseLineLandingPage} />}
      {!standaloneCruiseEnabled && canBookCruise && <RedirectWithStatus code={302} from="/:regionCode/cruises" to="/:regionCode/tours" />}
      {!standaloneCruiseEnabled && canBookCruise && <RedirectWithStatus code={302} from="/:regionCode/search/cruises" to="/:regionCode/tours" />}
      {!standaloneCruiseEnabled && canBookCruise && <RedirectWithStatus code={302} from="/:regionCode/cruise-line" to="/:regionCode/tours" />}
      {/* Car Hire Pages */}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <TrackedRoute exact path="/:regionCode/car-hire" component={CarHirePage} />}
      {/* Offer pages */}
      <TrackedRoute exact path="/:regionCode/offer/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </TrackedRoute>
      <TrackedRoute path="/:regionCode/offer/:offerSlug/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </TrackedRoute>
      {config.EXPERIENCES_STANDLONE_ENABLED && <TrackedRoute exact path="/:regionCode/experience/:id">
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </TrackedRoute>}
      {config.EXPERIENCES_STANDLONE_ENABLED && <TrackedRoute path="/:regionCode/experience/:slug/:id">
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </TrackedRoute>}
      <TrackedRoute exact path="/:regionCode/experiences/:locationSlug/:categorySlug" component={ExperiencesListPage} />
      <TrackedRoute exact path="/:regionCode/experiences/:locationSlug" component={ExperiencesListPage} />
      <TrackedRoute path="/:regionCode/partner/:offerSlug/:offerId">
        {({ match }) => <BedBankOfferPage key={match?.params.offerId} />}
      </TrackedRoute>
      <TrackedRoute path="/:regionCode/page/:pageNum" component={PaginatedOffersPage} />
      {/* If brand defines a particular default homepage, redirect "/" to that location */}
      {
        config.defaultHomePage && (
          <TrackedRoute
            exact
            path="/:regionCode/"
            component={({ location }: RouteComponentProps) => (
              <RedirectWithStatus
                code={302}
                to={location.pathname.replace(/$/, config.defaultHomePage)}
              />
            )}
          />
        )
      }
      <TrackedRoute
        exact
        path="/:regionCode/leprizecallback"
        render={() => {
          if (!isAdmin) {
            return (
              <HttpStatus code={404}>
                <Error404PageWithOffers />
              </HttpStatus>
            )
          }

          return <CallbackPage />
        }}
      />
      {isAdmin && <RedirectWithStatus code={301} exact from="/:regionCode/callback" to="/:regionCode/leprizecallback" />}
      {config.agentHub.isEnabled && <TrackedRoute exact path="/:regionCode/" component={AgentHubHomePage} />}
      {config.WHITELABEL_HOME_PAGE_ENABLED && <TrackedRoute exact path="/:regionCode/" component={WhitelabelHomePage} />}
      <TrackedRoute exact path="/:regionCode/" component={HomePage} />
      <RedirectWithStatus code={301} from="/:regionCode/villas" to="/:regionCode/holidays/homes-and-villas" />
      <RedirectWithStatus code={301} from="/:regionCode/doublesix" to="/:regionCode/offer/double-six-luxury-hotel-seminyak-bali-indonesia-asia/006RF00000KMiBlYAL" />
      {/* Support Pages */}
      {config.SUPPORT_ENABLED && <TrackedRoute path="/:regionCode/support" component={SupportPage} />}
      {/* Corporate Events */}
      {config.CORPORATE_EVENTS_ENABLED && <TrackedRoute path="/:regionCode/corporate-events/:eventId"><CorporateEventRoutes /></TrackedRoute>}
      {/* Weddings */}
      {config.WEDDINGS_ENABLED && <AuthRoute showPopup exact path="/:regionCode/weddings/create" component={WeddingSetupPage} />}
      {config.WEDDINGS_ENABLED && <AuthRoute showPopup exact path="/:regionCode/weddings/:slug/edit" component={WeddingSetupPage} />}
      {config.WEDDINGS_ENABLED && <TrackedRoute exact path="/:regionCode/weddings/:slug" component={WeddingPage} />}
      {/* LE Live */}
      {config.LE_LIVE_ENABLED && <TrackedRoute exact path="/:regionCode/live" component={LeLiveLandingPage} />}
      {/* Sales Team Contact Page */}
      <TrackedRoute path="/:regionCode/request-video-chat/:offerId/:packageId" component={VideoChatPage} />
      {/* LuxPlus */}
      {isLuxPlusEnabled &&
        <TrackedRoute path={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}/:tab?`} component={LuxPlusLandingPage} />}
      {isLuxPlusEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/luxplus" to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />}

      {/* Loyalty */}
      {isLoyaltyEnabled && (
        <TrackedRoute path={`/:regionCode/${LOYALTY_LANDING_PAGE}`}>
          <SocieteNavBar />
          <LocaleAwareSwitch>
            <TrackedRoute exact path={`/:regionCode/${TASTE_OF_GOLD_DETAILS_PAGE}`} component={TasteOfGoldDetailsPage} />
            <TrackedRoute exact path={`/:regionCode/${ESIM_DETAILS_PAGE}`} component={LoyaltyEsimDetailsPage} />
            <TrackedRoute exact path={`/:regionCode/${ESIM_INFO_PAGE}`} component={LoyaltyEsimInfoPage} />
            <TrackedRoute exact path={`/:regionCode/${TIER_PROGRESSION_DETAILS_PAGE}`} component={TierProgressionDetailsPage} />
            <TrackedRoute exact path={`/:regionCode/${LOUNGE_PASSES_PAGE}`} component={LoungePassesPage} />
            {isLoyaltyPurchasePointsEnabled &&
              <TrackedRoute exact path={`/:regionCode/${LOYALTY_PURCHASE_POINTS_PAGE}`} component={LoyaltyPurchasePointsPage} />}
            {isMaldivesCharterEnabled &&
              <TrackedRoute exact path={`/:regionCode/${LOYALTY_REWARDS_PAGE}`} component={LoyaltyRewardsPage} />}
            <TrackedRoute exact path={`/:regionCode/${LOYALTY_OFFERS_HUB_PAGE}`} component={LoyaltyOffersHubPage} />
            <TrackedRoute exact path={`/:regionCode/${TIER_UPGRADE_CAMPAIGN_PAGE}`} component={TierUpgradeCampaignPage} />
            <TrackedRoute path={`/:regionCode/${LOYALTY_LANDING_PAGE}`} component={LoyaltyLandingPage} />
          </LocaleAwareSwitch>
        </TrackedRoute>
      )}
      {isLoyaltyEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/society" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />}
      {isLoyaltyEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/société" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />}
      {isLoyaltyEnabled && <TrackedRoute path="/:regionCode/triple-points" component={TriplePointsTakeoverLandingPage} />}
      {isLoyaltyEnabled && <TrackedRoute path="/:regionCode/double-status-credits" component={DoublePointsTakeoverLandingPage} />}
      {/* Search Pages */}
      <TrackedRoute path="/:regionCode/search-fullscreen" component={HotelSearchFullScreenPage} />
      <TrackedRoute exact path="/:regionCode/search" component={HotelSearchPage} />
      <TrackedRoute exact path="/:regionCode/metasearch/:propertyId" component={MetaSearchPage} />
      <TrackedRoute exact path="/:regionCode/search/map" component={HotelSearchMapPage} />
      <TrackedRoute exact path="/:regionCode/search/store/map" component={HotelSearchStoreMapPage} />
      <TrackedRoute exact path="/:regionCode/search/homes-and-villas" component={HomesAndVillasSearchPage} />
      <TrackedRoute exact path="/:regionCode/search/homes-and-villas/map" component={HomesAndVillasSearchMapPage} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <TrackedRoute exact path="/:regionCode/search/experiences" component={ExperienceSearchPage} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <TrackedRoute exact path="/:regionCode/search/experiences/map" component={ExperienceSearchMapPage} />}
      {config.CRUISES_STANDLONE_ENABLED && canBookCruise && <TrackedRoute exact path="/:regionCode/search/cruises" component={CruiseSearchPage} />}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <TrackedRoute exact path="/:regionCode/search/car-hire" component={CarHireSearchPage} />}
      <RedirectWithStatus code={301} exact from="/velocity-frequent-flyer" to="/" />
      <RedirectWithStatus code={301} from="/:regionCode/velocity-frequent-flyer" to="/:regionCode/" />
      <RedirectWithStatus code={301} from="/:regionCode/chadstone.p1" to="/:regionCode/campaign/store" />
      <RedirectWithStatus code={301} from="/:regionCode/amex" to="/:regionCode/" />
      <RedirectWithStatus code={301} from="/:regionCode/refund-policy" to="/:regionCode/booking-policies" />
      <RedirectWithStatus code={301} from="/:regionCode/frequently-asked-questions" to="/:regionCode/support" />
      {/* Account pages */}
      <RedirectWithStatus code={301} from="/:regionCode/account/my-destinations" to="/:regionCode/account" />
      <AuthRoute showPopup exact path="/:regionCode/account/my-escapes/upgrade-room/:orderId" component={UpgradeRoomPage} />
      <AuthRoute exact path="/:regionCode/account" component={AccountPage} />
      {config.ENABLE_TRIP_PLANNER && <AuthRoute showPopup exact path="/:regionCode/wishlists" component={WishlistsIndexPage} />}
      {config.ENABLE_TRIP_PLANNER && <AuthRoute showPopup exact path="/:regionCode/wishlists/:wishlistId" component={WishlistDetailPage} />}
      <AuthRoute exact path="/:regionCode/account/credits-payments" component={CreditsAndPaymentPage} />
      <AuthRoute showPopup exact path="/:regionCode/account/vouchers" component={VouchersPage} />
      <AuthRoute showPopup path="/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher" component={ExperienceVoucher} />
      <AuthRoute showPopup exact path="/:regionCode/account/my-escapes/order/:orderId" component={OrderPage} />
      <AuthRoute showPopup exact path="/:regionCode/booking-authority/share" component={ShareOrderWebviewPage} />
      <AuthRoute showPopup exact path="/:regionCode/order/:orderId/experience-gift-details" component={ExperienceGiftPrintPage} />
      {isShopEnabled && <AuthRoute showPopup exact path="/:regionCode/account/shop-orders/order/:orderId" component={ShopOrderPage} />}
      <AuthRoute exact path="/:regionCode/account/travel-preferences" component={TravelPreferencesPage} />
      <AuthRoute exact path="/:regionCode/account/travel-companions" component={TravelCompanionsPage} />
      <AuthRoute showPopup exact path="/:regionCode/account/my-escapes/order/:orderId/airport-transfers/:experienceItemId" component={AirportTransferPage} />
      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RedirectWithStatus code={301} from="/:regionCode/my-escapes/:modalType?" to="/:regionCode/account/my-escapes/:modalType?" />
      <AuthRoute showPopup path="/:regionCode/account/my-escapes/:tab(upcoming|cancelled|past)?" component={MyEscapesPage} />
      {isShopEnabled && <AuthRoute showPopup path="/:regionCode/account/shop-orders/:tab(purchased|cancelled)?" component={ShopOrdersPage} />}
      <AuthRoute exact path="/:regionCode/account/details/traveller" component={AccountDetailsTravellerPage} />
      <AuthRoute exact path="/:regionCode/account/details" component={AccountDetailsPage} showPopup />
      <AuthRoute path="/:regionCode/booking-form/:orderId?" component={TourTravellerInfoPage} />
      <AuthRoute showPopup exact path="/:regionCode/review/:orderId?" component={UserReviews} />
      <TrackedRoute exact path="/:regionCode/reset-password/:resetPasswordToken" component={ResetPasswordPage} />
      {isLuxPlusEnabled && !isLoyaltyLuxplusLandingPageEnabled && <LuxPlusMemberRoute showPopup exact path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} component={LuxPlusAccountPage} />}
      {isLuxPlusEnabled && isLoyaltyLuxplusLandingPageEnabled && <RedirectWithStatus code={301} from={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />}

      {/* hidden preference centre page */}
      <AuthRoute showPopup path="/:regionCode/account/hidden-communication-preferences" component={config.BRAND === LUXURY_ESCAPES ? MySubscriptionsPage : MyWLSubscriptionsPage} />
      {/* Subscriptions Pages */}
      <TrackedRoute exact path="/:regionCode/resubscribe" component={ResubscribePage} />
      <TrackedRoute
        exact
        path="/:regionCode/account/communication-preferences/unsubscribe"
        render={() => {
          if (!MULTI_CLICK_UNSUB_SUPPORTED_BRANDS.includes(config.BRAND)) {
            return (
              <HttpStatus code={404}>
                <Error404PageWithOffers />
              </HttpStatus>
            )
          }

          return <MultiClickUnsubPage />
        }}
      />
      <AuthRoute path="/:regionCode/account/communication-preferences" component={PreferenceCentrePage} />
      <RedirectWithStatus code={301} from="/:regionCode/my-subscriptions" to="/:regionCode/account/communication-preferences" />
      <RedirectWithStatus code={301} from="/:regionCode/account/my-subscriptions" to="/:regionCode/account/communication-preferences" />
      <RedirectWithStatus code={301} from="/:regionCode/account/subscriptions" to="/:regionCode/account/communication-preferences" />
      <RedirectWithStatus code={301} exact from="/:regionCode/account/payment-cards" to="/:regionCode/account/credits-payments" />
      <RedirectWithStatus code={301} from="/:regionCode/account-details" to="/:regionCode/account/details" />
      {/* Insurance pages */}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/quote" component={TravelInsuranceQuote} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/add" component={TravelInsuranceAddCheckout} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/details" component={TravelInsuranceDetails} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/payment" component={TravelInsurancePayment} />}
      <AuthRoute showPopup path="/:regionCode/passport-card-insurance/details" component={PassportCardDetailsPage} />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/checkout" to="/:regionCode/travel-protection/quote" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/confirmation" to="/:regionCode/booking/confirmation" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance" to="/:regionCode/travel-protection" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/xcover" to="/:regionCode/travel-protection/xcover" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/quote" to="/:regionCode/travel-protection/quote" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/add" to="/:regionCode/travel-protection/add" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/details" to="/:regionCode/travel-protection/details" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/payment" to="/:regionCode/travel-protection/payment" />
      {/* Gift card pages */}
      <RedirectWithStatus code={301} from="/:regionCode/giftcards" to="/:regionCode/gift-cards" />
      <RedirectWithStatus code={301} from="/:regionCode/giftcard" to="/:regionCode/gift-cards" />
      <RedirectWithStatus code={301} from="/:regionCode/gift-card" to="/:regionCode/gift-cards" exact />
      <AuthRoute path="/:regionCode/gift-cards/confirmation" component={GiftCardConfirmationPage} />
      {/* Purchase credits page */}
      {isSpoofed && <TrackedRoute path="/:regionCode/purchase-credits" component={PurchaseCreditsPage} />}
      <AuthRoute showPopup path="/:regionCode/gift-card/:code" component={GiftCardViewPage} />
      {/* Booking pages */}
      <AuthRoute showPopup path="/:regionCode/booking/confirmation" component={ConfirmationPage} />
      <AuthRoute showPopup path="/:regionCode/bb/booking/confirmation" component={ConfirmationPage} />
      <RedirectWithStatus code={301} from="/:regionCode/confirmation/:orderId" to="/:regionCode/booking/confirmation?orderId=:orderId" />
      <RedirectWithStatus code={301} from="/:regionCode/bb/booking/confirmation" to="/:regionCode/booking/confirmation" />
      <RedirectWithStatus code={301} from="/:regionCode/bb/booking/:step" to="/:regionCode/booking/:step" />
      {/* Redeem gift hotel and experience */}
      <TrackedRoute exact path="/:regionCode/redeem/:redeemCode/:orderId/:offerId" component={RedeemGifted} />
      {/* TourV2Pages */}
      <TrackedRoute exact path="/:regionCode/tour/:offerId">
        {({ match }) => <TourV2OfferPage key={match?.params.offerId} />}
      </TrackedRoute>
      <TrackedRoute exact path="/:regionCode/tour/:offerId/:variationId" component={TourV2OfferVariationPage} />
      <TrackedRoute exact path="/:regionCode/search/tours" component={TourV2SearchPage} />
      <TrackedRoute exact path="/:regionCode/tours/deals/:slug" component={TourDealPage} />
      {/* Tour signature series contentful redirects */}
      <RedirectWithStatus code={301} from="/:regionCode/signature-series.p1" to="/:regionCode/campaign/signature-series" />
      <RedirectWithStatus code={301} from="/:regionCode/indigo" to="/:regionCode/offer/hotel-indigo-melbourne-little-collins-victoria-australia/006RF00000MZ9gTYAT" />
      {/* Tailor-Made Tours */}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && (
        <TrackedRoute exact path="/:regionCode/tailor-made-tours/requests/:requestId" component={TailorMadeTourRequestConfirmationPage} />
      )}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && (
        <TrackedRoute exact path="/:regionCode/tailor-made-tours/requests" component={TailorMadeTourRequestPage} />
      )}
      <TrackedRoute exact path="/:regionCode/tours/tours-catalogue" component={TourCataloguePage} />
      {config.hasReferralProgram && (
        <TrackedRoute exact path="/:regionCode/refer" component={ReferPage} />
      )}
      {config.hasReferralProgram && (
        <TrackedRoute path="/:regionCode/refer/:referralCode" component={RefereeLandingPage} />
      )}
      <TrackedRoute path="/:regionCode/google-hotel-ads" component={GoogleHotelAdPage} />
      <RedirectWithStatus code={302} from="/:regionCode/property/ayodya-resort-bali-bedbank:6486e2f1-958e-4f91-a5b0-481205904898" to="/:regionCode/offer/ayodya-resort-bali-nusa-dua-indonesia-asia/0062y00000JN0RvAAL" />
      <RedirectWithStatus code={302} from="/:regionCode/abercrombie-and-kent-tours.p1" to="/:regionCode/tours" />
      <TrackedRoute path="/:regionCode/property/:propertyNameAndId" component={PropertyPage} />
      {config.ENABLE_AU_COMPETITION && <TrackedRoute path="/:regionCode/hunter-valley-competition" component={AuCompetitionsPage} />}
      {config.ENABLE_INTL_COMPETITION && <TrackedRoute path="/:regionCode/spin-the-wheel-competition" component={IntlCompetitionsPage} />}
      <TrackedRoute path="/:regionCode/NHCollectionDubaiThePalm" component={ukCompetitionsPage} />
      <RedirectWithStatus code={301} from="/:regionCode/citi" to="/:regionCode/save-with-citi.p1" />
      <RedirectWithStatus code={301} from="/:regionCode/terms" to="/:regionCode/terms-and-conditions" />
      <TrackedRoute
        path="/:regionCode/sharethis/verify"
        component={(): null => {
          const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
          const script = document.createElement('script')
          script.setAttribute('id', 'sharethis-js')
          script.src = mainUrl
          script.async = true
          document.body.appendChild(script)
          return null
        }}
      />
      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <RedirectWithStatus code={301} from="/:regionCode/pages/:slug" to="/:regionCode/:slug.p1" />}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <TrackedRoute exact path="/:regionCode/:slug.p1" component={ContentHub} />}
      {config.staticPages.map(sp => (
        sp.url?.to && <TrackedRoute
          exact
          key={sp.url.to}
          path={`/:regionCode${sp.url.to}`}
          render={props => {
            if (sp.type === 'bestPrice') {
              return <BestPricePage />
            } else if (sp.type === 'aboutUs') {
              return <AboutUsPage />
            } else if (sp.type === 'careersAndCulture') {
              return <CareersPage />
            }
            else if (sp.type === 'termsAndConditions') {
              return <LegalPages slug="terms-and-conditions" location={props.location} />
            } else if (sp.type === 'luxPlusTermsAndConditions') {
              return <LegalPages slug={LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE} location={props.location} />
            } else if (sp.type === 'societeTermsAndConditions') {
              return <LegalPages slug="societe-terms-and-conditions" location={props.location} />
            } else if (sp.type === 'societeFAQs') {
              return <LegalPages slug="societe-frequently-asked-questions" location={props.location} />
            } else if (sp.type === 'shopTermsAndConditions') {
              return <LegalPages slug="shop-terms-and-conditions" location={props.location} />
            } else if (sp.type === 'shopFAQs') {
              return <LegalPages slug="shop-frequently-asked-questions" location={props.location} />
            } else if (sp.type === 'refundPolicy') {
              return <LegalPages slug="refund-policy" location={props.location} />
            } else if (sp.type === 'news') {
              return <NewsPage slug="news" location={props.location} />
            } else if (sp.type === 'privacyPolicy') {
              return <LegalPages slug="privacy-policy" location={props.location} />
            } else if (sp.type === 'giftcardTermsAndConditions') {
              return <LegalPages slug="giftcards-terms" location={props.location} />
            } else if (sp.type === 'flights') {
              return <LegalPages slug="flights-policies" location={props.location} />
            } else if (sp.type === 'frequentFlyerPrograms') {
              return <FrequentFlyerPage slug="frequent-flyer-programs" location={props.location} />
            } else if (sp.type === 'specialProductsPromotions') {
              return <LegalPages slug="promotions" location={props.location} />
            } else if (sp.type === 'bookingPolicies') {
              return <LegalPages slug="booking-policies" location={props.location} />
            } else if (sp.type === 'bestPriceTerms') {
              return <LegalPages slug="best-price-guarantee-terms" location={props.location} />
            } else if (sp.type === 'feeSchedule') {
              return <LegalPages slug="fee-schedule" location={props.location} />
            } else if (sp.type === 'travelInsurance') {
              return <TravelInsurancePage />
            } else if (sp.type === 'travelInsuranceCoverGenius') {
              return <TravelInsuranceCoverGeniusPage />
            } else if (sp.type === 'bnblLanding') {
              return <BNBLLandingPage />
            } else if (sp.type === 'twcLanding') {
              return <TWCLandingPage />
            } else if (sp.type === 'ewcLanding') {
              return <EWCLandingPage />
            } else if (sp.type === 'affiliatePage') {
              return <ContentfulWebPage />
            } else {
              return <StaticPage type={sp.type} title={typeof sp.text === 'string' ? sp.text : intl.formatMessage(sp.text)} />
            }
          }}
        />
      ))}
      {/* Promotional Pages */}
      {!standaloneFlightsEnabled && <TrackedRoute path="/:regionCode/flights" component={FlightsLandingPage} />}
      {standaloneFlightsEnabled && <TrackedRoute exact path="/:regionCode/flights/:dealCategory?" component={FlightsSearchLandingPage} />}
      {standaloneFlightsEnabled && <TrackedRoute path="/:regionCode/flights-search" component={FlightsSearchPage} />}
      {standaloneFlightsEnabled && <TrackedRoute path="/:regionCode/flights-search-results" component={FlightsSearchResultsPage} />}
      {standaloneFlightsEnabled && <TrackedRoute path="/:regionCode/flights-search-results-multi-city" component={FlightsSearchMultiCityResultsPage} />}
      <TrackedRoute path="/:regionCode/flight/deal/:id" component={FlightDealLandingPage} />
      <TrackedRoute path="/:regionCode/flights/campaigns/:id" component={FlightCampaignsLandingPage} />
      <TrackedRoute path="/:regionCode/escapes-finder" component={EscapesFinderPage} />
      <RedirectWithStatus code={301} from="/:regionCode/disneyland-offers" to="/:regionCode/experience/led%3A0062y00000Gui6KAAR" />
      <RedirectWithStatus code={301} from="/:regionCode/disneyland-offers-test" to="/:regionCode/experience/led%3A0062y000009EEOEAA4" />
      <RedirectWithStatus from="/:regionCode/spotify" to="/:regionCode/campaign/spotify" />
      <RedirectWithStatus from="/:regionCode/nyc" to="/:regionCode/campaign/new-york" />
      <RedirectWithStatus from="/:regionCode/traveltester" to="/:regionCode/campaign/7news-traveltester" />
      <TrackedRoute
        exact
        path="/:regionCode/friends/:referralEmail"
        render={({ match }) => (
          <AppRedirect to={`/${match.params.regionCode}/campaign/${CHARTER_PAGE_SLUG}?${FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM}=${encodeURIComponent(match.params.referralEmail ?? '')}`} />
        )}
      />
      {config.promotionalPages.map(pp => {
        const PromotionalComponent = promotionalPages[pp.component]
        return <TrackedRoute
          key={pp.url}
          path={`/:regionCode${pp.url}`}
        >
          <PromotionalComponent />
        </TrackedRoute>
      })}
      {config.holidayPages &&
        <RedirectWithStatus code={301} from="/:regionCode/holidays/:slug" to="/:regionCode/:slug.h1" />}
      {config.holidayPages &&
        <TrackedRoute path="/:regionCode/:slug.h1" component={(props: ComponentProps<typeof LandingPage>) => <LandingPage {...props} typeOfPage={TYPE_OF_PAGE_HOLIDAY} />} />}
      {config.destinationPages &&
        <RedirectWithStatus code={301} from="/:regionCode/:slug.d1" to="/:regionCode/destination/:slug" />}
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting" to="/:regionCode/campaign/lux-list-travel-awards" />
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-uk" to="/:regionCode/campaign/lux-list-travel-awards-uk" />
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-us" to="/:regionCode/campaign/lux-list-travel-awards-us" />
      {/* Redirect due to wrong URL sent out */}
      <RedirectWithStatus code={301} from="/:regionCode/campaign/singapore/competition-april" to="/:regionCode/campaign/competition-april" />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards" component={LuxAwardPage} />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards-prizes" component={LuxAwardPage} />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards-us" component={LuxAwardPage} />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards-prizes-us" component={LuxAwardPage} />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards-uk" component={LuxAwardPage} />
      <TrackedRoute exact path="/:regionCode/campaign/lux-list-travel-awards-prizes-uk" component={LuxAwardPage} />
      <AuthRoute
        showPopup
        exact
        path={`/:regionCode/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`}
        render={() => {
          if (!isStaff || !isFriendsAndFamilyPageEnabled) {
            return (
              <HttpStatus code={404}>
                <Error404PageWithOffers />
              </HttpStatus>
            )
          }

          return <FriendsAndFamilyInvitePage />
        }}
      />
      <TrackedRoute exact path={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}`} component={MaldivesDirectSearchPage} />
      <TrackedRoute exact path={`/:regionCode/campaign/${CHARTER_BEFORE_YOU_FLY_PAGE_SLUG}`} component={MaldivesDirectBeforeYouFlyPage} />
      <TrackedRoute path="/:regionCode/destination/:campaignName*" component={ContentfulWebPage} />
      <TrackedRoute path="/:regionCode/campaign/:campaignName*" component={ContentfulWebPage} />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/maldives" to="/:regionCode/destination/maldives" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/bali" to="/:regionCode/destination/bali" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/greece" to="/:regionCode/destination/greece" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/spain" to="/:regionCode/destination/spain" />
      {/* New URL Structure For SEO Destination/Product pages */}
      <TrackedRoute exact path="/:regionCode/:productType/country/:country" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:productType/city/:city/:state/:country" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:productType/city/:city/:country" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:productType/location/:location/:city/:state/:country" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:productType/location/:location/:city/:country" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:productType/location/:location/:country" component={DestinationProductPage} />
      {/* HolidayPackage Pages: Rule of URL: [base]/holiday-packages/:location/:holidayType */}
      <TrackedRoute exact path="/:regionCode/holiday-packages/:location/:holidayType" component={DestinationLandingPage} />
      <TrackedRoute exact path="/:regionCode/holiday-packages/:location" component={DestinationLandingPage} />
      <TrackedRoute exact path="/:regionCode/resorts/:location" component={DestinationLandingPage} />
      {/* SEO Destination/Product pages */}
      <TrackedRoute exact path="/:regionCode/:location.:holidayType.:productType" component={DestinationProductPage} />
      <TrackedRoute exact path="/:regionCode/:locationOrHolidayType.:productType" component={DestinationProductPage} />
      {config.LERE_ENABLE_TOP_PICKS_FOR_ME && <TrackedRoute path="/:regionCode/top-picks-for-me" component={TopPicksForMePage} />}
      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-payment/:orderId/:type(deposit-balance|instalment-balance|instalment|reserve-balance|custom-offer|custom-offer-v2|payment-schedule-balance)"
        component={AncillaryPaymentPage}
      />
      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-change-reservation/:orderId/:type(change-dates|change-package|tour-optional-experiences)/:itemId?"
        component={AncillaryChangeReservationPage}
      />
      <AuthRoute showPopup path="/:regionCode/checkout/bnbl/order/:orderId/item/:itemId" component={RedirectCheckoutBNBL} />
      <AuthRoute showPopup path="/:regionCode/checkout/change-dates/order/:orderId/item/:itemId" component={RedirectCheckoutChangeDates} />
      <AuthRoute showPopup path="/:regionCode/checkout/extend-dates/order/:orderId/item/:itemId" component={RedirectCheckoutExtendDates} />
      <AuthRoute showPopup path="/:regionCode/checkout/add-extra-guests/order/:orderId/item/:itemId" component={RedirectCheckoutAddExtraGuests} />
      <AuthRoute showPopup path="/:regionCode/checkout-restore-cart" component={CheckoutRestoreCartPage} />
      <AuthRoute showPopup path="/:regionCode/checkout-gateway" component={CheckoutGatewayPage} />
      <TrackedRoute exact path="/:regionCode/checkout/:cartId/:step?" component={CheckoutPageRoutes} />
      <TrackedRoute path="/:regionCode/checkout/:step?" component={CheckoutPageRoutes} />
      <TrackedRoute path="/:regionCode/trip-planner">
        <TripPlannerRoutes />
      </TrackedRoute>
      <AuthRoute showPopup exact path="/:regionCode/cart" component={UserCartPage} />
      <AuthRoute showPopup accountAccessModalInitialMode="sharedCartInvite" exact path="/:regionCode/cart/s/:sharedCartId" component={SharedCartInvitePage} />
      <RedirectWithStatus code={301} from="/:regionCode/user-cart" to="/:regionCode/cart" />
      <TrackedRoute exact path="/:regionCode/hotels/:holidayType" component={HolidayTypeLandingPage} />
      <TrackedRoute exact path="/:regionCode/hotels/:holidayType/:country" component={HolidayTypeLandingPage} />
      <TrackedRoute exact path="/:regionCode/hotels/:holidayType/:country/:cityOrState" component={HolidayTypeLandingPage} />
      <TrackedRoute
        exact
        path="/:regionCode/orders/:orderId/accept-invite/:invitationId"
        component={AcceptOrderInvitePage}
      />
      {config.SECURE_PAYMENT_ENABLED &&
        <TrackedRoute exact path="/:regionCode/pay/:paymentLinkId" component={SecurePaymentPage} />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount &&
        <TrackedRoute exact path={`/:regionCode${LE_AGENT_HUB_SOCIETE_CONFIRMATION}`} component={SocieteConfirmationPage} />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount &&
        <TrackedRoute exact path={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`} component={AgentHubAccountSetupCompleted} />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount &&
        <RedirectWithStatus
          from={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/complete-account-setup`}
          to={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`}
        />}
      {config.SPOOFING_SEND_CONFIRMATION_EMAIL && <TrackedRoute path="/:regionCode/skycheck" component={SkyCheckConfirmationPage} />}
      {isShopEnabled && (
        <TrackedRoute path={['/:regionCode/shop', '/:regionCode/search/shop']}>
          <LocaleAwareSwitch>
            <TrackedRoute exact path="/:regionCode/shop" component={MerchandiseNavBar} />
            <TrackedRoute exact path="/:regionCode/shop/category/:categorySlug" component={MerchandiseNavBar} />
            <TrackedRoute exact path="/:regionCode/search/shop/brands/:brandSlug/:brandId" component={MerchandiseNavBar} />
            <TrackedRoute exact path="/:regionCode/search/shop/:parentCategorySlug/:categorySlug" component={MerchandiseNavBar} />
            <TrackedRoute exact path="/:regionCode/search/shop/:categorySlug?" component={MerchandiseNavBar} />
          </LocaleAwareSwitch>
          <LocaleAwareSwitch>
            <TrackedRoute exact path="/:regionCode/shop" component={MerchandiseLandingPage} />
            <TrackedRoute exact path="/:regionCode/shop/offer/:offerId" component={MerchandiseOfferPage} />
            <TrackedRoute exact path="/:regionCode/shop/offer/:slug/:offerId" component={MerchandiseOfferPage} />
            <TrackedRoute exact path="/:regionCode/shop/brands" component={MerchandiseBrandsPage} />
            <TrackedRoute exact path="/:regionCode/shop/categories" component={MerchandiseCategoriesPage} />
            <TrackedRoute exact path="/:regionCode/shop/category/:categorySlug" component={MerchandiseCategoryPage} />
            <TrackedRoute exact path="/:regionCode/search/shop/brands/:brandSlug/:brandId" component={MerchandiseSearchPage} />
            <TrackedRoute exact path="/:regionCode/search/shop/:parentCategorySlug/:categorySlug" component={MerchandiseSearchPage} />
            <TrackedRoute exact path="/:regionCode/search/shop/:categorySlug?" component={MerchandiseSearchPage} />
          </LocaleAwareSwitch>
        </TrackedRoute>
      )}
      {/* Mobile app marketing url, redirect this to home page until we have one */}
      <RedirectWithStatus code={302} from="/:regionCode/app" to="/:regionCode/" />
      <TrackedRoute>
        <HttpStatus code={404}>
          <Error404PageWithOffers />
        </HttpStatus>
      </TrackedRoute>
    </LocaleAwareSwitch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currentRegionCode: state.geo.currentRegionCode,
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state) || isFlightsCredit(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
}

export default connect(mapStateToProps)(PageRoutes)
